import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Container,
    Grid2,
    Paper,
    Snackbar,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ModernNavbar from '../setup/ModernNavbar';
import Footer from '../setup/Footer';
import AutoComplete from '../../BuiltInComponent/AutoComplete';
import { getUnits } from '../../features/AdminManagement/unitsSlice';
import { getEmployeeForList } from '../../features/PayrollManagement/employeeSlice';
import {
    addAttendanceSite,
    deleteAttendanceSite,
    editAttendanceSite,
    getAttendanceSites,
    getEmployeeSiteAssignments,
    resetAttendanceSiteState,
    saveEmployeeSiteAssignments
} from '../../features/AdminManagement/attendanceSiteSlice';

const initialForm = {
    siteID: '',
    siteName: '',
    unitID: '',
    latitude: '',
    longitude: '',
    radiusMeters: '100',
    status: true
};

export default function AttendanceSites() {
    const dispatch = useDispatch();
    const { sites, assignments, isLoading, isError, isSuccess, message } = useSelector((state) => state.attendanceSites);
    const { units } = useSelector((state) => state.units);
    const { empForList } = useSelector((state) => state.emp);
    const [form, setForm] = React.useState(initialForm);
    const [selectedSiteID, setSelectedSiteID] = React.useState('');
    const [selectedEmployees, setSelectedEmployees] = React.useState([]);
    const [toastOpen, setToastOpen] = React.useState(false);

    React.useEffect(() => {
        dispatch(getAttendanceSites());
        dispatch(getEmployeeSiteAssignments());
        dispatch(getUnits());
        dispatch(getEmployeeForList());
    }, [dispatch]);

    React.useEffect(() => {
        if (isSuccess || isError) {
            setToastOpen(true);
        }

        if (isSuccess) {
            dispatch(getAttendanceSites());
            dispatch(getEmployeeSiteAssignments());
        }
    }, [dispatch, isSuccess, isError]);

    React.useEffect(() => {
        if (!selectedSiteID) {
            setSelectedEmployees([]);
            return;
        }

        const mappedIds = assignments.filter((item) => Number(item.siteID) === Number(selectedSiteID) && item.empID).map((item) => Number(item.empID));
        setSelectedEmployees((empForList || []).filter((item) => mappedIds.includes(Number(item.empID))));
    }, [assignments, selectedSiteID, empForList]);

    const closeToast = () => {
        setToastOpen(false);
        dispatch(resetAttendanceSiteState());
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleUnitChange = (data) => {
        if (!data) {
            setForm((prev) => ({ ...prev, unitID: '' }));
            return;
        }

        const keys = Object.keys(data);
        setForm((prev) => ({ ...prev, unitID: data[keys[0]] }));
    };

    const handleEditClick = (site) => {
        setForm({
            siteID: site.siteID,
            siteName: site.siteName,
            unitID: site.unitID || '',
            latitude: String(site.latitude),
            longitude: String(site.longitude),
            radiusMeters: String(site.radiusMeters),
            status: Boolean(site.status)
        });
    };

    const handleDeleteClick = async (siteID) => {
        await dispatch(deleteAttendanceSite(siteID));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const payload = {
            ...form,
            latitude: Number(form.latitude),
            longitude: Number(form.longitude),
            radiusMeters: Number(form.radiusMeters),
            unitID: form.unitID || null,
            status: form.status ? 1 : 0
        };

        if (form.siteID) {
            await dispatch(editAttendanceSite(payload));
        } else {
            await dispatch(addAttendanceSite(payload));
        }

        setForm(initialForm);
    };

    const handleSaveAssignments = async () => {
        if (!selectedSiteID) {
            return;
        }

        await dispatch(saveEmployeeSiteAssignments({
            siteID: selectedSiteID,
            employeeIDs: selectedEmployees.map((item) => item.empID)
        }));
    };

    return (
        <>
            <ModernNavbar />
            <Toolbar />
            <Container sx={{ pb: 4 }}>
                <Typography sx={{ textAlign: 'center', fontSize: 30, fontWeight: 'bolder', mb: 3 }}>Attendance Sites</Typography>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 2 }}>Create / Update Site</Typography>
                    <Box component="form" onSubmit={handleSubmit}>
                        <Grid2 container spacing={2}>
                            <Grid2 size={4}>
                                <TextField fullWidth size="small" label="Site Name" name="siteName" value={form.siteName} onChange={handleChange} />
                            </Grid2>
                            <Grid2 size={4}>
                                <AutoComplete
                                    listData={units || []}
                                    value={(units || []).find((item) => item.unitID === form.unitID) || null}
                                    onChange={handleUnitChange}
                                    label="Unit (Optional)"
                                />
                            </Grid2>
                            <Grid2 size={2}>
                                <TextField fullWidth size="small" label="Latitude" name="latitude" value={form.latitude} onChange={handleChange} />
                            </Grid2>
                            <Grid2 size={2}>
                                <TextField fullWidth size="small" label="Longitude" name="longitude" value={form.longitude} onChange={handleChange} />
                            </Grid2>
                            <Grid2 size={3}>
                                <TextField fullWidth size="small" label="Radius (Meters)" name="radiusMeters" value={form.radiusMeters} onChange={handleChange} />
                            </Grid2>
                            <Grid2 size={3}>
                                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                    <Switch checked={form.status} onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.checked }))} />
                                    <Typography>{form.status ? 'Active' : 'Inactive'}</Typography>
                                </Box>
                            </Grid2>
                            <Grid2 size={6}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <Button variant="outlined" onClick={() => setForm(initialForm)}>Reset</Button>
                                    <Button variant="contained" type="submit" disabled={isLoading}>{form.siteID ? 'Update Site' : 'Create Site'}</Button>
                                </Box>
                            </Grid2>
                        </Grid2>
                    </Box>
                </Paper>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 2 }}>Assign Employees To Site</Typography>
                    <Grid2 container spacing={2}>
                        <Grid2 size={4}>
                            <Autocomplete
                                options={sites || []}
                                value={(sites || []).find((site) => Number(site.siteID) === Number(selectedSiteID)) || null}
                                getOptionLabel={(option) => option.siteName || ''}
                                onChange={(_event, value) => setSelectedSiteID(value?.siteID || '')}
                                renderInput={(params) => <TextField {...params} label="Select Site" size="small" />}
                            />
                        </Grid2>
                        <Grid2 size={8}>
                            <Autocomplete
                                multiple
                                options={empForList || []}
                                value={selectedEmployees}
                                getOptionLabel={(option) => option.empName || ''}
                                isOptionEqualToValue={(option, value) => option.empID === value.empID}
                                onChange={(_event, value) => setSelectedEmployees(value || [])}
                                renderInput={(params) => <TextField {...params} label="Assigned Employees" size="small" />}
                            />
                        </Grid2>
                        <Grid2 size={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button variant="contained" onClick={handleSaveAssignments} disabled={!selectedSiteID || isLoading}>Save Assignments</Button>
                            </Box>
                        </Grid2>
                    </Grid2>
                </Paper>

                <Paper sx={{ p: 3 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 2 }}>Existing Sites</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Site</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Unit</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Coordinates</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Radius</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Assigned</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(sites || []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">No attendance sites found.</TableCell>
                                    </TableRow>
                                ) : (sites || []).map((site) => (
                                    <TableRow key={site.siteID} hover>
                                        <TableCell>{site.siteName}</TableCell>
                                        <TableCell>{site.unitDescription || '-'}</TableCell>
                                        <TableCell>{Number(site.latitude).toFixed(6)}, {Number(site.longitude).toFixed(6)}</TableCell>
                                        <TableCell>{Number(site.radiusMeters).toFixed(2)} m</TableCell>
                                        <TableCell>{site.assignedEmployeesCount || 0}</TableCell>
                                        <TableCell><Chip size="small" label={site.status ? 'Active' : 'Inactive'} color={site.status ? 'success' : 'default'} /></TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button size="small" variant="outlined" onClick={() => handleEditClick(site)}>Edit</Button>
                                                <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteClick(site.siteID)}>Delete</Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Snackbar open={toastOpen} autoHideDuration={3500} onClose={closeToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={closeToast} severity={isError ? 'error' : 'success'} sx={{ width: '100%' }}>{message || 'Done.'}</Alert>
                </Snackbar>
            </Container>
            <Footer />
        </>
    );
}